import { defineStore } from 'pinia';
import { computed, ref, watch, watchEffect } from 'vue';
import { AttendStatus, ClassDetails, ClassGroupInfo, ClassItemProps, ClassStatus, ClassStudentInfo, ClassViewMode, RankedGroup, RankedStudent } from '../types/class';
import { classService } from '../services';
import { utils } from '../utils';
import { SignalRGroups, SignalRRoom, SignalRUserMap } from '../types/signalr';
import { UserRole } from '../types/user';
import { useUserStore } from './user';
import { useSessionStore } from './session';
import { Session } from '../types/session';
import { useLectureStore } from './lecture';
import defaultAvatar from '../assets/imgs/isaac_avatar.png'


export const useClassStore = defineStore('class', () => {
    const user = useUserStore()
    const sessionStore = useSessionStore()
    const lecture = useLectureStore()

    const teacherClassId = ref<number>(0)
    const teacherClassSystem = ref<string>('')
    const selectedClassDetails = ref<ClassDetails | null>(null)
    const requestedStudentIds = ref<number[]>([])
    const raiseHandStudentIds = ref<number[]>([])
    const roomName = computed<string>(() => selectedClassDetails.value?.id ? `${selectedClassDetails.value.system}-iclass-${selectedClassDetails.value.id}` : '')
    const viewMode = ref<ClassViewMode>(ClassViewMode.management)
    const isClassDetailCollapsed = ref<boolean>(selectedClassDetails.value?.state === ClassStatus.on_going ? true : false);

    const isLockedScreen = computed(() => {
        if (selectedClassDetails.value?.lockScreen) {
            if (sessionStore.currentSession !== Session.vocab) {
                return true
            }

            if (!sessionStore.allowSpeakingStudentIds.includes(user.userInfo?.id || 0)) {
                return true
            }
        }

        return false
    })

    const studentGroups = computed<ClassGroupInfo[]>(() => {
        const groups = selectedClassDetails.value?.groups?.map<ClassGroupInfo>(group => {
            return {
                id: group.id,
                name: group.name,
                point: group.point,
                students: []
            }
        }) || []
        const students = selectedClassDetails.value?.students || []
        for (const group of groups) {
            for (const student of students) {
                if (group.id !== student.group_id) continue;
                if (!group.students) group.students = []
                group.students.push(student)
            }
        }
        return groups
    })

    const attendedStudents = computed<ClassStudentInfo[]>(() => selectedClassDetails.value?.students?.filter(student => student.status === AttendStatus.attended) || []);

    const attendedStudentCount = computed(() => {
        return attendedStudents.value.length
    })

    const rankedGroups = computed(() => {
        // Calculate total points and sort
        const allPointsZero = studentGroups.value.every(group => utils.calculateGroupTotalPoint(group) === 0);
        if (allPointsZero) {
            return [];
        }
        const rankedGroups: RankedGroup[] = JSON.parse(JSON.stringify(studentGroups.value)).filter((group: RankedGroup) => utils.calculateGroupTotalPoint(group) > 0);
        rankedGroups.sort((a, b) => utils.calculateGroupTotalPoint(b) - utils.calculateGroupTotalPoint(a));
        let currentRank = 1;
        for (let i = 0; i < rankedGroups.length; i++) {
            const currentGroup = rankedGroups[i]
            const nextGroup = rankedGroups[i + 1]
            currentGroup.rank = currentRank

            if (!nextGroup || utils.calculateGroupTotalPoint(currentGroup) === utils.calculateGroupTotalPoint(nextGroup)) {
                continue
            }
            currentRank++
        }
        return rankedGroups;
    })

    const rankedStudents = computed(() => {
        const students = selectedClassDetails.value?.students;
        if (!students) return [];
        const allPointsZero = students.every(student => utils.calculateTotalPoint(student.point) === 0);
        if (allPointsZero) {
            return [];
        }
        // Calculate total points and sort students
        const rankedStudents: RankedStudent[] = JSON.parse(JSON.stringify(selectedClassDetails.value?.students || [])).filter((student: RankedStudent) => student.status == AttendStatus.attended)
        rankedStudents.sort((a, b) => utils.calculateTotalPoint(b.point) - utils.calculateTotalPoint(a.point))
        let currentRank = 1
        for (let i = 0; i < rankedStudents.length; i++) {

            const currentStudent = rankedStudents[i]
            const nextStudent = rankedStudents[i + 1]
            currentStudent.rank = utils.calculateTotalPoint(currentStudent.point) == 0 ? 0 : currentRank

            if (!nextStudent || utils.calculateTotalPoint(currentStudent.point) === utils.calculateTotalPoint(nextStudent.point)) {
                continue
            }

            currentRank++
        }
        return rankedStudents;
    });

    const currentStudent = computed<ClassStudentInfo | null>(() => {
        if (user.userRole !== UserRole.student) return null;
        return selectedClassDetails.value?.students?.find(student => student.username === user.username) || null
    })

    function getClassScheduleFormat(classItem: ClassItemProps | null) {
        if (!classItem) return '';

        const dateFormat = utils.parseTimestampToDateWithMonthName(classItem.start_date)
        const startTimeFormat = utils.formatTimeStringNoSeconds(classItem.start_time)
        const endTimeFormat = utils.formatTimeStringNoSeconds(classItem.end_time)
        return `${dateFormat} ${startTimeFormat} - ${endTimeFormat}`
    }

    async function getTeacherClassDetails() {
        try {
            const classDetails = await classService.getTeacherClassDetails(teacherClassId.value, teacherClassSystem.value)
            //default avatar for no avatar user
            if (classDetails && classDetails.students) {
                const updatedStudents = classDetails.students.map((student) => ({
                    ...student,
                    avatar: student.avatar ?? defaultAvatar,
                }));
                classDetails.students = updatedStudents;
            }
            selectedClassDetails.value = classDetails
        } catch {
            teacherClassId.value = 0
            teacherClassSystem.value = ''
        }
    }

    async function getStudentClassDetails() {
        const classDetails = await classService.getStudentClassDetails()

        //default avatar for no avatar user
        if (classDetails && classDetails.students) {
            const updatedStudents = classDetails.students.map((student) => ({
                ...student,
                avatar: student.avatar ?? defaultAvatar,
            }));
            classDetails.students = updatedStudents;
        }

        selectedClassDetails.value = classDetails
    }

    function getClassGroupNameById(groupId: number | null): string {
        const group = selectedClassDetails.value?.groups?.find(group => group.id === groupId)
        return group?.name || ''
    }

    function clearClassInfo() {
        // clear class details
        selectedClassDetails.value = null
        // clear teacher class id
        teacherClassId.value = 0
        // clear teacher class system
        teacherClassSystem.value = ''
        // clear view mode
        viewMode.value = ClassViewMode.management
        // clear raise hand
        raiseHandStudentIds.value = []
        // clear current screen
        sessionStore.currentSession = Object.values(Session)[0]
        // clear session data
        sessionStore.classContent = null
        // clear session vocab
        sessionStore.currentVocabId = 0
        // clear lecture data
        lecture.currentQuestionIndex = 0
    }

    function syncClassStudents(roomUsers: SignalRUserMap) {
        if (!roomUsers) return;
        if (!selectedClassDetails.value) return;

        const classStudents: ClassStudentInfo[] = JSON.parse(JSON.stringify(selectedClassDetails.value.students || []))
        if (!classStudents.length) return;

        for (const student of classStudents) {
            const roomUser = roomUsers[student.username]
            if (!roomUser) {
                student.connected = false
                continue;
            }

            student.connected = roomUser.connected
            student.group_id = roomUser.groupId
            student.point = roomUser.point
            student.quizQuestion = roomUser.quizQuestion || []
            student.isSubmitLecture = roomUser.isSubmitLecture
            student.lectureQuestions = roomUser.lectureQuestion || []
            student.status = AttendStatus.attended

            if (user.userRole === UserRole.student && student.id === user.userInfo?.id) {
                selectedClassDetails.value.student_group_id = roomUser.groupId
            }
        }

        selectedClassDetails.value.students = classStudents
    }

    function syncClass(room: SignalRRoom) {
        if (!selectedClassDetails.value) return;
        selectedClassDetails.value = {
            ...selectedClassDetails.value,
            state: room.state,
            lockScreen: room.lockScreen,
            allowRaiseHand: room.allowRaiseHand,
            expiredTime: room.expiredTime
        }
        raiseHandStudentIds.value = room.listRaiseHand.map(user => parseInt(user.id))
        requestedStudentIds.value = room.listRequestJoinRoom.map(user => parseInt(user.id))
    }

    function syncGroupPoints(groups: SignalRGroups) {
        if (!selectedClassDetails.value) return;
        for (const classGroup of selectedClassDetails.value.groups) {
            if (!groups[classGroup.id]) continue;
            classGroup.point = groups[classGroup.id].point
        }
    }

    watchEffect(() => {
        if (!teacherClassId.value || !teacherClassSystem.value) return;
        getTeacherClassDetails()
    })

    watch(() => selectedClassDetails.value?.id, () => {
        if (!selectedClassDetails.value?.id) return;
        // get class content
        sessionStore.getClassContent()
    })

    watch(() => selectedClassDetails.value?.state, () => {
        if (selectedClassDetails.value?.state === ClassStatus.on_going) {
            isClassDetailCollapsed.value = true
        }
    })

    return {
        teacherClassId,
        teacherClassSystem,
        selectedClassDetails,
        requestedStudentIds,
        raiseHandStudentIds,
        roomName,
        viewMode,
        isLockedScreen,
        studentGroups,
        attendedStudentCount,
        rankedGroups,
        rankedStudents,
        isClassDetailCollapsed,
        currentStudent,
        attendedStudents,
        getClassScheduleFormat,
        getTeacherClassDetails,
        getStudentClassDetails,
        getClassGroupNameById,
        clearClassInfo,
        syncClassStudents,
        syncClass,
        syncGroupPoints
    }
}, {
    persist: {
        pick: ['teacherClassId', 'teacherClassSystem']
    }
})